import { AddToCollectionConfirmation } from "./AddToCollectionConfirmation";
import { ApprovalMode } from "./approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "./ClientTools";
import {
  ToolErrorKind,
  classifyToolFailure,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import {
  collectionMemberIds,
  collectionMissingIds,
  describeId,
  findTypeMismatches,
  toResourceRefs,
} from "./collectionResourceIds";
import {
  MAX_PROPOSED_IDS,
  coerceAddToCollectionInput,
  describeCollectionFailure,
  isAddToCollectionParams,
  type AddToCollectionParams,
} from "./collectionToolInput";

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    collection_id: {
      type: "string",
      description: "ID of the collection to modify. Always begins with 'cl_'.",
    },
    add: {
      type: "array",
      items: { type: "string" },
      maxItems: MAX_PROPOSED_IDS,
      description:
        "Bare ID strings to add — not {resource_id, resource_type} objects. " +
        "Must match what the collection holds: dataset IDs ('ds_') for a " +
        "dataset collection, file IDs ('fl_') for a file collection, event " +
        `IDs ('ev_') for an event collection. At most ${MAX_PROPOSED_IDS} IDs ` +
        "across 'add' and 'remove' in one call.",
    },
    remove: {
      type: "array",
      items: { type: "string" },
      maxItems: MAX_PROPOSED_IDS,
      description: "IDs to remove, in the same form as 'add'.",
    },
  },
  required: ["collection_id"],
};

async function execute(
  params: AddToCollectionParams,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  // Not TRANSPORT: nothing was attempted, and no retry can supply what the
  // browser context is missing.
  if (!deps?.collectionService || !deps?.orgId) {
    return toolError(
      "Collections are unavailable in this session, so this tool cannot run. " +
        "Nothing was attempted.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  // The same verdict the confirmation modal reached, so the user cannot approve
  // a proposal that is refused here.
  const verdict = coerceAddToCollectionInput(params);
  if (!verdict.ok) {
    return toolError(
      verdict.reason,
      verdict.details ?? {},
      ToolErrorKind.VALIDATION,
    );
  }
  const { collectionId, add, remove } = verdict.payload;

  let collection;
  try {
    collection = await deps.collectionService.getCollectionById(collectionId);
  } catch (err) {
    return toolError(
      `Could not load collection ${collectionId}: ${describeCollectionFailure(err)}`,
      {},
      classifyToolFailure(err),
    );
  }

  // The collection's own type is authoritative; members are homogeneous. Catch
  // a mismatch here so the model gets a specific message instead of the API's
  // generic rejection.
  const resourceType = collection.resource_type;
  const mismatched = findTypeMismatches([...add, ...remove], resourceType);
  if (mismatched.length > 0) {
    return toolError(
      `Collection ${collectionId} holds ${resourceType}s, but ` +
        `${mismatched.map(describeId).join("; ")}.`,
      { collection_resource_type: resourceType, rejected_ids: mismatched },
      ToolErrorKind.VALIDATION,
    );
  }

  const membersBefore = new Set(collectionMemberIds(collection));

  // Only the service call belongs in the transport `try`. A throw from anything
  // after it would otherwise be reported as an unknown outcome even though the
  // write demonstrably landed.
  let updated;
  try {
    updated = await deps.collectionService.updateCollection(
      collectionId,
      {
        ...(add.length > 0 && {
          add_resources: toResourceRefs(add, resourceType),
        }),
        ...(remove.length > 0 && {
          remove_resources: toResourceRefs(remove, resourceType),
        }),
      },
      { resourceOwnerId: deps.orgId },
    );
  } catch (err) {
    return toolError(
      `Failed to update collection ${collectionId}: ${describeCollectionFailure(err)}`,
      {},
      classifyToolFailure(err),
    );
  }

  try {
    deps.onCollectionMembersChanged?.(updated);
  } catch {
    // A stale cache is not a failed write.
  }

  // Real deltas: an add is an upsert and a remove is a `DELETE ... WHERE` that
  // matches nothing for a non-member, so only before-vs-after membership says
  // what changed. An ID the server cannot resolve lands in `missing` instead of
  // becoming a member.
  const members = new Set(collectionMemberIds(updated));
  const unresolved = new Set(collectionMissingIds(updated));
  const added = add.filter((id) => members.has(id) && !membersBefore.has(id));
  const alreadyMembers = add.filter(
    (id) => members.has(id) && membersBefore.has(id),
  );
  const notAdded = add.filter((id) => !members.has(id));
  const removed = remove.filter(
    (id) => membersBefore.has(id) && !members.has(id),
  );
  const stillMembers = remove.filter((id) => members.has(id));
  const alreadyAbsent = remove.filter(
    (id) => !membersBefore.has(id) && !members.has(id),
  );

  return toolSuccess("updated_collection", {
    added,
    collection_id: updated.collection_id,
    collection_name: updated.name,
    member_count: members.size,
    removed,
    resource_type: resourceType,
    version: updated.version,
    ...(alreadyMembers.length > 0 && { already_members: alreadyMembers }),
    // Per ID: one unresolved entry must not relabel the whole list, since the
    // two reasons call for different follow-up.
    ...(notAdded.length > 0 && {
      not_added: Object.fromEntries(
        notAdded.map((id): [string, string] => [
          id,
          unresolved.has(id)
            ? "could not be resolved (unknown, deleted, or in another org)"
            : "not a member of the collection after the update",
        ]),
      ),
    }),
    ...(alreadyAbsent.length > 0 && { already_absent: alreadyAbsent }),
    ...(stillMembers.length > 0 && { still_members: stillMembers }),
  });
}

export const tool = createClientTool<AddToCollectionParams>({
  name: "client_add_to_collection",
  description:
    "Add or remove members of an existing Roboto collection. A collection " +
    "holds one kind of resource — datasets, files, or events — so every ID " +
    "passed must be of that kind; the type is taken from each ID's prefix. " +
    "Use list_related or get_entity first to find the collection and the IDs " +
    "to move. Adding an ID that is already a member is a no-op.",
  input_schema: inputSchema,
  validateInput: isAddToCollectionParams,
  execute,
  defaultApprovalMode: ApprovalMode.REQUIRE_APPROVAL,
  // Pinned to approval-only: this mutates a curated artifact, and the
  // confirmation is the whole mitigation against the model getting it wrong.
  // A user must not be able to turn it off from the settings modal.
  allowableApprovalModes: [ApprovalMode.REQUIRE_APPROVAL],
  displayName: "Edit Collection",
  displayDescription: "Adds or removes members of an existing collection.",
  ConfirmationContent: AddToCollectionConfirmation,
});
